import { workspacePrefix } from "./lib/workspaceAssertion";
/**
 * App — the whole route table for the funnel workspace.
 *
 * SCOPE. Every route below belongs to one tool: the funnel builder, its
 * orders, its analytics and its live board. There is intentionally no
 * dashboard, no settings tree and no unrelated modules — this package is the
 * funnel OS, not a general admin shell. Add your own routes as siblings of
 * the `/app` block; keep the funnel block's paths as-is, because the pages
 * navigate to these literals internally.
 *
 * PATH PREFIX. The workspace base is `/app/production/listicle-builder`.
 * That string appears in `ListicleBuildersLayout` and in navigate() calls
 * across the pages, so changing it here alone will break in-app links —
 * grep for the literal before you move it.
 *
 * CODE-SPLITTING. Every route element is lazy so the initial bundle stays
 * small; the providers, shell, ProtectedRoute, LoginPage and Toaster are
 * statically imported because they are needed on first paint.
 */
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AccessProvider } from "./contexts/AccessContext";
import { DateRangeProvider } from "./context/DateRangeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import EmbedBridge from "./components/EmbedBridge";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/LoginPage";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

const ListicleBuildersLayout = lazy(() => import("./pages/listiclebuilders/ListicleBuildersLayout"));
const LBDashboardPage = lazy(() => import("./pages/listiclebuilders/LBDashboardPage"));
const LBWebsitesPage = lazy(() => import("./pages/listiclebuilders/LBWebsitesPage"));
const LBNewWebsiteWizard = lazy(() => import("./pages/listiclebuilders/LBNewWebsiteWizard"));
const LBWebsiteWorkspace = lazy(() => import("./pages/listiclebuilders/LBWebsiteWorkspace"));
const LBTemplatesPage = lazy(() => import("./pages/listiclebuilders/LBTemplatesPage"));
const LBOrdersPage = lazy(() => import("./pages/listiclebuilders/LBOrdersPage"));
const LBSubscriptionsPanel = lazy(() => import("./pages/listiclebuilders/LBSubscriptionsPanel"));
const LBAbandonedCheckoutsPage = lazy(() => import("./pages/listiclebuilders/LBAbandonedCheckoutsPage"));
const LBCustomerProfilePage = lazy(() => import("./pages/listiclebuilders/LBCustomerProfilePage"));
const LBOrderDetailPage = lazy(() => import("./pages/listiclebuilders/LBOrderDetailPage"));
const LBFailedPaymentsPage = lazy(() => import("./pages/listiclebuilders/LBFailedPaymentsPage"));
const LBAnalyticsPage = lazy(() => import("./pages/listiclebuilders/LBAnalyticsPage"));
const LBSettingsPage = lazy(() => import("./pages/listiclebuilders/LBSettingsPage"));
const LBStorePaymentsPage = lazy(() => import("./pages/listiclebuilders/LBStorePaymentsPage"));
const LBStoreLocalPricesPage = lazy(() => import("./pages/listiclebuilders/LBStoreLocalPricesPage"));
const LBCostsPage = lazy(() => import("./pages/listiclebuilders/LBCostsPage"));
const LiveViewPage = lazy(() => import("./pages/listiclebuilders/LiveViewPage"));

// Subscription stack wave-1 operator screens (lane BI1 wiring).
const LBTaxesPage = lazy(() => import("./pages/listiclebuilders/LBTaxesPage"));
const LBRetentionPlansPage = lazy(() => import("./pages/listiclebuilders/LBRetentionPlansPage"));
const LBRetentionChargesPage = lazy(() => import("./pages/listiclebuilders/LBRetentionChargesPage"));
// Owner-only portal-admin embeds (lane E2C, GUIDE §3.6.3).
const LBPortalAdminFrame = lazy(() => import("./pages/listiclebuilders/LBPortalAdminFrame"));
// Failed Payment Recovery studio (B03, GUIDE §1 8.3), wired by lane BI2.
const LBFprStudioPage = lazy(() => import("./pages/listiclebuilders/LBFprStudioPage"));
const LBFprStrategyWizard = lazy(() => import("./pages/listiclebuilders/LBFprStrategyWizard"));
const LBFprStrategyDetailPage = lazy(() => import("./pages/listiclebuilders/LBFprStrategyDetailPage"));
// Retention Hub (A11): the catalog index and one page per dashboard key.
const RetentionDashboardsIndex = lazy(() => import("./retention/pages/RetentionDashboardsIndex"));
const RetentionSubscriptionsOverview = lazy(() => import("./retention/pages/SubscriptionsOverviewPage"));
const RetentionSubscriptionChurn = lazy(() => import("./retention/pages/SubscriptionChurnPage"));
const RetentionRenewalRate = lazy(() => import("./retention/pages/RenewalRatePage"));
const RetentionSubscriptionCohort = lazy(() => import("./retention/pages/SubscriptionCohortPerformancePage"));
const RetentionSubscriptionJourney = lazy(() => import("./retention/pages/SubscriptionJourneyPage"));
const RetentionUpcomingOrders = lazy(() => import("./retention/pages/UpcomingOrdersPage"));
const RetentionCustomerOverview = lazy(() => import("./retention/pages/CustomerOverviewPage"));
const RetentionCustomerChurn = lazy(() => import("./retention/pages/CustomerChurnPage"));
const RetentionCustomerCohort = lazy(() => import("./retention/pages/CustomerCohortPerformancePage"));
const RetentionCustomerActions = lazy(() => import("./retention/pages/CustomerActionsPage"));
const RetentionRevenueOverview = lazy(() => import("./retention/pages/RevenueOverviewPage"));
const RetentionLifetimeRevenue = lazy(() => import("./retention/pages/LifetimeRevenuePage"));
const RetentionCheckout = lazy(() => import("./retention/pages/CheckoutPage"));
const RetentionOrderErrors = lazy(() => import("./retention/pages/OrderErrorsPage"));
const RetentionCancellationPrevention = lazy(() => import("./retention/pages/CancellationPreventionPerformancePage"));
const RetentionFailedPaymentRecovery = lazy(() => import("./retention/pages/FailedPaymentRecoveryPerformancePage"));
const RetentionHome = lazy(() => import("./retention/pages/HomePage"));
const RetentionReports = lazy(() => import("./retention/pages/ReportsPage"));
// Lane U12 (Ludo 2026-10-15): retention/analytics, the one new page.
const RetentionSubscriptionAnalytics = lazy(() => import("./retention/pages/SubscriptionAnalyticsPage"));

/** Route-level fallback. Never blanks the shell — only the outlet area. */
function RouteFallback() {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-muted-foreground" data-testid="route-loading">
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AccessProvider>
        <DateRangeProvider>
          <BrowserRouter basename={workspacePrefix(window.location.pathname) || "/"}>
            <Toaster />
            <EmbedBridge />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route element={<ProtectedRoute />}>
                  <Route path="/app" element={<AppShell />}>
                    <Route index element={<Navigate to="production/listicle-builder" replace />} />

                    <Route path="production/listicle-builder" element={<ListicleBuildersLayout />}>
                      <Route index element={<LBDashboardPage />} />
                      <Route path="websites" element={<LBWebsitesPage />} />
                      <Route path="websites/new" element={<LBNewWebsiteWizard />} />
                      <Route path="websites/:wid/*" element={<LBWebsiteWorkspace />} />
                      <Route path="templates" element={<LBTemplatesPage />} />
                      <Route path="orders" element={<LBOrdersPage />} />
                      <Route path="orders/subscriptions" element={<LBSubscriptionsPanel />} />
                      <Route path="orders/abandoned" element={<LBAbandonedCheckoutsPage />} />
                      <Route path="orders/customers" element={<LBCustomerProfilePage />} />
                      <Route path="orders/customers/:email" element={<LBCustomerProfilePage />} />
                      <Route path="orders/failed-payments" element={<LBFailedPaymentsPage />} />
                      {/* React Router v6 ranks by specificity, not source order,
                          so the static siblings above (subscriptions, abandoned,
                          customers, failed-payments) always win over :sessionId.
                          Listed last anyway so the precedence reads correctly. */}
                      <Route path="orders/:sessionId" element={<LBOrderDetailPage />} />
                      <Route path="analytics" element={<LBAnalyticsPage />} />
                      <Route path="settings" element={<LBSettingsPage />} />
                      {/* Store → Payments (S2-UI). Both paths mount the same
                          screen: without a store id it falls back to the
                          workspace's first store, so a link that predates a
                          store id still lands somewhere useful. */}
                      <Route path="stores/payments" element={<LBStorePaymentsPage />} />
                      <Route path="stores/:storeId/payments" element={<LBStorePaymentsPage />} />
                      {/* Frozen local price table (Ludo 2026-10-02), reached
                          from the store's payments settings. */}
                      <Route path="stores/local-prices" element={<LBStoreLocalPricesPage />} />
                      <Route path="stores/:storeId/local-prices" element={<LBStoreLocalPricesPage />} />
                      <Route path="costs" element={<LBCostsPage />} />
                      {/* Subscription stack wave-1 screens (lane BI1). */}
                      <Route path="taxes" element={<LBTaxesPage />} />
                      <Route path="retention" element={<RetentionDashboardsIndex />} />
                      <Route path="retention/plans" element={<LBRetentionPlansPage />} />
                      <Route path="retention/charges" element={<LBRetentionChargesPage />} />
                      {/* Owner-only portal-admin embeds (lane E2C, GUIDE §3.6.3). */}
                      <Route path="retention/flow-builder" element={<LBPortalAdminFrame path="/ops/portal/retention" title="Cancel flow builder" />} />
                      {/* Lane U5: the portal serves the Cancel Reasons page at
                          /ops-portal-cancel-reasons (GUIDE §3.6.3); any other
                          /ops/portal/* path falls back to the Flow Builder. */}
                      <Route path="retention/cancel-reasons" element={<LBPortalAdminFrame path="/ops-portal-cancel-reasons" title="Cancel reasons" />} />
                      {/* Failed Payment Recovery studio (B03, GUIDE §1 8.3). */}
                      <Route path="retention/recovery" element={<LBFprStudioPage />} />
                      <Route path="retention/recovery/new" element={<LBFprStrategyWizard />} />
                      <Route path="retention/recovery/:id" element={<LBFprStrategyDetailPage />} />
                      <Route path="retention/subscriptions-overview" element={<RetentionSubscriptionsOverview />} />
                      <Route path="retention/subscription-churn" element={<RetentionSubscriptionChurn />} />
                      <Route path="retention/renewal-rate" element={<RetentionRenewalRate />} />
                      <Route path="retention/subscription-cohort-performance" element={<RetentionSubscriptionCohort />} />
                      <Route path="retention/subscription-journey" element={<RetentionSubscriptionJourney />} />
                      <Route path="retention/upcoming-orders" element={<RetentionUpcomingOrders />} />
                      <Route path="retention/customer-overview" element={<RetentionCustomerOverview />} />
                      <Route path="retention/customer-churn" element={<RetentionCustomerChurn />} />
                      <Route path="retention/customer-cohort-performance" element={<RetentionCustomerCohort />} />
                      <Route path="retention/customer-actions" element={<RetentionCustomerActions />} />
                      <Route path="retention/revenue-overview" element={<RetentionRevenueOverview />} />
                      <Route path="retention/lifetime-revenue" element={<RetentionLifetimeRevenue />} />
                      <Route path="retention/checkout" element={<RetentionCheckout />} />
                      <Route path="retention/order-errors" element={<RetentionOrderErrors />} />
                      <Route path="retention/cancellation-prevention-performance" element={<RetentionCancellationPrevention />} />
                      <Route path="retention/failed-payment-recovery-performance" element={<RetentionFailedPaymentRecovery />} />
                      <Route path="retention/home" element={<RetentionHome />} />
                      <Route path="retention/reports" element={<RetentionReports />} />
                      {/* Lane U12 (Ludo 2026-10-15): Retention -> Subscription analytics. */}
                      <Route path="retention/analytics" element={<RetentionSubscriptionAnalytics />} />
                    </Route>

                    {/* Live View is a SIBLING of the layout, not a child: it is a
                        full-bleed board and must render without the workspace
                        rail. Keep it outside the block above. */}
                    <Route path="production/listicle-builder/live" element={<LiveViewPage />} />
                  </Route>
                </Route>

                <Route path="/" element={<Navigate to="/app/production/listicle-builder" replace />} />
                <Route path="*" element={<Navigate to="/app/production/listicle-builder" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </DateRangeProvider>
      </AccessProvider>
    </AuthProvider>
  );
}
